import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Spinner, Alert, Form } from 'react-bootstrap';
import { ChromePicker } from 'react-color';
import axios from 'axios';
import { EventColor, Event } from '../interfaces';
import MessageModal from './MessageModal';
import ConfirmationModal from './ConfirmationModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faEdit,
  faTrash,
  faSave,
  faPalette
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../contexts/AuthContext';

import { API_BASE_URL, buildApiUrl } from '../config/api';

const EventColorList: React.FC = () => {
    const { user } = useAuth();
    const [colors, setColors] = useState<EventColor[]>([]);
    const [events, setEvents] = useState<Event[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    const [showForm, setShowForm] = useState<boolean>(false);
    const [colorToEdit, setColorToEdit] = useState<EventColor | null>(null);
    const [colorName, setColorName] = useState<string>('');
    const [hexCode, setHexCode] = useState<string>('#FFFFFF');
    const [displayColorPicker, setDisplayColorPicker] = useState<boolean>(false);

    const [showMessageModal, setShowMessageModal] = useState<boolean>(false);
    const [messageModalTitle, setMessageModalTitle] = useState<string>('');
    const [messageModalContent, setMessageModalContent] = useState<string>('');
    const [messageModalVariant, setMessageModalVariant] = useState<'success' | 'danger' | 'info' | 'warning'>('info');

    const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
    const [confirmModalMessage, setConfirmModalMessage] = useState<string>('');
    const [confirmAction, setConfirmAction] = useState<(() => void) | null>(null);
    const [confirmModalTitle, setConfirmModalTitle] = useState<string>('');

    const fetchColorsAndEvents = async () => {
        setLoading(true);
        setError(null);
        try {
            // Fetch colors and events concurrently
            const [colorsRes, eventsRes] = await Promise.all([
                axios.get<EventColor[]>(buildApiUrl('events/event-colors/')),
                axios.get<Event[]>(buildApiUrl('events/events/'))
            ]);
            setColors(colorsRes.data);
            setEvents(eventsRes.data);
        } catch (err) {
            setError("Failed to load data (colors and events).");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchColorsAndEvents();
    }, []);

    const showMessage = (title: string, message: string, variant: 'success' | 'danger' | 'info' | 'warning' = 'info') => {
        setMessageModalTitle(title);
        setMessageModalContent(message);
        setMessageModalVariant(variant);
        setShowMessageModal(true);
    };
    
    // חדש: פונקציה למציאת אירוע משויך
    const getAssociatedEventName = (colorId: number | undefined): string | null => {
        if (!colorId) return null;
        const associatedEvent = events.find(event => event.color === colorId && event.status !== 'completed' && event.status !== 'cancelled');
        return associatedEvent ? associatedEvent.event_name : null;
    };

    const handleAddClick = () => {
        setColorToEdit(null);
        setColorName('');
        setHexCode('#FFFFFF');
        setShowForm(true);
    };

    const handleEditClick = (color: EventColor) => {
        setColorToEdit(color);
        setColorName(color.name);
        setHexCode(color.hex_code);
        setShowForm(true);
    };

    const handleFormSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        try {
            const payload = { name: colorName, hex_code: hexCode };
            if (colorToEdit) {
                await axios.put(`${buildApiUrl('events/event-colors/')}colorToEdit.id}/`, payload);
                showMessage('הצלחה', `הצבע "${colorToEdit.name}" עודכן ל-"${colorName}" (${hexCode}) בהצלחה!`, 'success');
            } else {
                await axios.post(buildApiUrl('events/event-colors/'), payload);
                showMessage('הצלחה', `נוצר הצבע החדש "${colorName}" (${hexCode}) בהצלחה!`, 'success');
            }
            setShowForm(false);
            fetchColorsAndEvents();
        } catch (err) {
            console.error("שגיאה בשמירת צבע:", err);
            showMessage('שגיאה', 'שגיאה בשמירת הצבע. ייתכן שקוד הצבע כבר קיים.', 'danger');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteClick = (id: number) => {
        setConfirmModalTitle('אישור מחיקה');
        setConfirmModalMessage('האם אתה בטוח שברצונך למחוק צבע זה?');
        setConfirmAction(() => async () => {
            try {
                const color = colors.find(c => c.id === id);
                await axios.delete(buildApiUrl(`events/event-colors/${id}/`));
                showMessage('הצלחה', `הצבע ${color ? `"${color.name}" (${color.hex_code}) ` : ''}נמחק בהצלחה!`, 'success');
                fetchColorsAndEvents();
            } catch (err) {
                showMessage('שגיאה', 'שגיאה במחיקת הצבע. וודא שאינו משויך לאירוע.', 'danger');
            }
        });
        setShowConfirmModal(true);
    };

    const handleColorPickerClick = () => {
        setDisplayColorPicker(!displayColorPicker);
    };

    const handleColorPickerClose = () => {
        setDisplayColorPicker(false);
    };

    const handleColorChange = (color: any) => {
        setHexCode(color.hex);
    };

    const popover = {
        position: 'absolute' as 'absolute',
        zIndex: '2',
    };
    const cover = {
        position: 'fixed' as 'fixed',
        top: '0px',
        right: '0px',
        bottom: '0px',
        left: '0px',
    };

    return (
        <div>
            <div className="d-flex justify-content-between align-items-center mb-4 app-sticky-page-toolbar">
                <h2>ניהול צבעי אירועים</h2>
                        <Button variant="success" onClick={handleAddClick}>
          <FontAwesomeIcon icon={faPlus} className="me-1" />
          הוסף צבע חדש
        </Button>
            </div>

            {loading && <Spinner animation="border" />}
            {error && <Alert variant="danger">{error}</Alert>}

            {!loading && !error && (
                !colors || colors.length === 0 ? (
                    <p>אין צבעים מוגדרים במערכת.</p>
                ) : (
                    <Table striped bordered hover responsive>
                        <thead>
                            <tr>
                                <th>שם הצבע</th>
                                <th>תצוגה</th>
                                <th>קוד Hex</th>
                                <th>משויך לאירוע</th>
                                <th>פעולות</th>
                            </tr>
                        </thead>
                        <tbody>
                            {colors.map(color => {
                                const associatedEventName = getAssociatedEventName(color.id);
                                return (
                                    <tr key={color.id}>
                                        <td>{color.name}</td>
                                        <td>
                                            <div
                                                style={{
                                                    width: '30px',
                                                    height: '30px',
                                                    backgroundColor: color.hex_code,
                                                    border: '1px solid #ccc',
                                                    borderRadius: '5px',
                                                }}
                                            ></div>
                                        </td>
                                        <td>{color.hex_code}</td>
                                        <td>
                                            {associatedEventName ? (
                                                <span className="text-danger fw-bold">{associatedEventName} (תפוס)</span>
                                            ) : (
                                                <span className="text-success">פנוי</span>
                                            )}
                                        </td>
                                        <td>
                                                                    <Button variant="warning" size="sm" className="me-2" onClick={() => handleEditClick(color)}>
                          <FontAwesomeIcon icon={faEdit} className="me-1" />
                          ערוך
                        </Button>
                        <Button variant="danger" size="sm" onClick={() => handleDeleteClick(color.id!)} disabled={user?.role?.name === 'WAREHOUSE'} title={user?.role?.name === 'WAREHOUSE' ? 'למחסנאי אין הרשאה למחיקה' : ''}>
                          <FontAwesomeIcon icon={faTrash} className="me-1" />
                          מחק
                        </Button>
                                        </td>
                                    </tr>
                                )
                            })}
                        </tbody>
                    </Table>
                )
            )}

            <Modal show={showForm} onHide={() => setShowForm(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>{colorToEdit ? 'עריכת צבע' : 'הוספת צבע חדש'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form onSubmit={handleFormSubmit}>
                        <Form.Group className="mb-3" controlId="formColorName">
                            <Form.Label>שם הצבע</Form.Label>
                            <Form.Control type="text" value={colorName} onChange={e => setColorName(e.target.value)} required />
                        </Form.Group>
                        <Form.Group className="mb-3" controlId="formHexCode">
                            <Form.Label>קוד צבע Hex</Form.Label>
                            <div className="d-flex align-items-center">
                                <Form.Control type="text" value={hexCode} onChange={e => setHexCode(e.target.value)} required readOnly />
                                <div
                                    style={{
                                        width: '36px',
                                        height: '36px',
                                        backgroundColor: hexCode,
                                        border: '1px solid #ccc',
                                        borderRadius: '5px',
                                        cursor: 'pointer',
                                        marginLeft: '10px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: hexCode === '#FFFFFF' ? '#000' : '#fff'
                                    }}
                                    onClick={handleColorPickerClick}
                                    title="בחר צבע"
                                >
                                    <FontAwesomeIcon icon={faPalette} size="sm" />
                                </div>
                            </div>
                            {displayColorPicker && (
                                <div style={popover}>
                                    <div style={cover} onClick={handleColorPickerClose} />
                                    <ChromePicker color={hexCode} onChange={handleColorChange} />
                                </div>
                            )}
                        </Form.Group>
                        <Button variant="primary" type="submit" disabled={loading}>
                            {loading ? <Spinner animation="border" size="sm" /> : <><FontAwesomeIcon icon={faSave} className="me-1" /> שמור</>}
                        </Button>
                    </Form>
                </Modal.Body>
            </Modal>

            <MessageModal show={showMessageModal} onHide={() => setShowMessageModal(false)} title={messageModalTitle} message={messageModalContent} variant={messageModalVariant} />
            <ConfirmationModal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} title={confirmModalTitle} message={confirmModalMessage} onConfirm={confirmAction || (() => {})} />
        </div>
    );
};

export default EventColorList;
